import { useRef, useEffect } from "react";
import { createLogger } from "../../../utils/logger";

const log = createLogger("ScrollPositionPreservation");

// Module-level storage — survives component unmount/remount
const scrollPositionStore = new Map<string, number>();

/**
 * Preserves scroll position per agent across agent switches.
 *
 * Saves the container's scrollTop to a module-level Map keyed by agentId, and
 * restores it when that agent's thread is shown again. It used to cover tab
 * navigation too, but chat is now the task pane's only surface, so agent
 * switching (and remounts) is the only thing left that moves the scroll.
 */
export function useScrollPositionPreservation(
  agentId: string,
  containerRef: React.RefObject<HTMLDivElement | null>
): void {
  const previousAgentIdRef = useRef(agentId);

  // Save scroll position on unmount (e.g., the pane closing or remounting)
  useEffect(() => {
    return () => {
      if (containerRef.current) {
        scrollPositionStore.set(agentId, containerRef.current.scrollTop);
        log.debug("Saved scroll position on unmount", { agentId });
      }
    };
  }, [agentId, containerRef]);

  // Restore scroll position on mount (e.g., the pane reopening)
  useEffect(() => {
    const saved = scrollPositionStore.get(agentId);
    if (saved !== undefined && containerRef.current) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = saved;
          log.debug("Restored scroll position on mount", { agentId, scrollTop: saved });
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount only

  // Save/restore on agent switch
  useEffect(() => {
    if (previousAgentIdRef.current !== agentId) {
      // Save for departing agent
      if (containerRef.current) {
        scrollPositionStore.set(previousAgentIdRef.current, containerRef.current.scrollTop);
        log.debug("Saved scroll position for departing agent", {
          agentId: previousAgentIdRef.current,
        });
      }

      // Restore for incoming agent
      requestAnimationFrame(() => {
        const saved = scrollPositionStore.get(agentId);
        if (containerRef.current && saved !== undefined) {
          containerRef.current.scrollTop = saved;
          log.debug("Restored scroll position for incoming agent", {
            agentId,
            scrollTop: saved,
          });
        }
      });

      previousAgentIdRef.current = agentId;
    }
  }, [agentId, containerRef]);
}

/** Clears the module-level store. Exported for testing only. */
export function _resetStoreForTesting(): void {
  scrollPositionStore.clear();
}
